import type { DebugLogEntry } from './types';

let buffer: DebugLogEntry[] = [];
let maxSize = 1000;
let writeIndex = 0;
let isFull = false;
let initialized = false;

const originalConsole: Record<string, (...args: unknown[]) => void> = {};

function serializeArg(arg: unknown): string {
  try {
    if (arg === null) return 'null';
    if (arg === undefined) return 'undefined';
    if (typeof arg === 'string') return arg;
    if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
    if (arg instanceof Error) {
      return `${arg.name}: ${arg.message}${arg.stack ? '\n' + arg.stack : ''}`;
    }
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

function addEntry(type: DebugLogEntry['type'], args: unknown[]): void {
  const entry: DebugLogEntry = {
    type,
    time: new Date().toISOString(),
    args: args.map(serializeArg),
  };

  buffer[writeIndex] = entry;
  writeIndex = (writeIndex + 1) % maxSize;
  if (writeIndex === 0) isFull = true;

  // Update window reference
  window.__debugLogs = getOrderedLogs();
}

function getOrderedLogs(): DebugLogEntry[] {
  if (!isFull) {
    return buffer.slice(0, writeIndex);
  }
  // Ring buffer is full: return from writeIndex to end, then start to writeIndex
  return [...buffer.slice(writeIndex), ...buffer.slice(0, writeIndex)];
}

export function initConsoleCapture(size = 1000): void {
  if (initialized) return;
  initialized = true;
  maxSize = size;
  buffer = new Array(maxSize);

  const methods: DebugLogEntry['type'][] = ['log', 'warn', 'error', 'info', 'debug'];

  methods.forEach((method) => {
    originalConsole[method] = console[method].bind(console);
    console[method] = (...args: unknown[]) => {
      addEntry(method, args);
      originalConsole[method](...args);
    };
  });

  window.__debugLogs = [];
}

export function getDebugLogs(): DebugLogEntry[] {
  return getOrderedLogs();
}

export function clearDebugLogs(): void {
  buffer = new Array(maxSize);
  writeIndex = 0;
  isFull = false;
  window.__debugLogs = [];
}
